import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type { PlanSnapshot, UUID } from './types';

interface BackendSnapshot {
  plan_id: UUID;
  json_payload: Record<string, unknown>;
  calc_engine_version: string;
  created_at: string;
}

/**
 * Snapshot is read from the plan via GET /plan/{id}/snapshot.
 * Backend returns a thin wrapper with `json_payload` containing the calc result;
 * for now we expose the wrapper through `PlanSnapshot` keys only when present.
 */
export function useSnapshot(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.snapshot.plan(planId) : ['snapshot', 'none'],
    queryFn: async (): Promise<PlanSnapshot | null> => {
      if (!planId) return null;
      try {
        const res = await apiClient.get<BackendSnapshot>(`/plan/${planId}/snapshot`);
        const payload = res.data.json_payload ?? {};
        // Engine liefert direkt einnahmen_jahr / ausgaben_jahr / jahresergebnis
        // und monatsergebnisse als {1..12 → {...}}. KPIs werden daraus abgeleitet.
        const num = (v: unknown): number => {
          if (typeof v === 'number') return v;
          if (typeof v === 'string') {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : 0;
          }
          return 0;
        };
        const einnahmenJahr = num(payload.einnahmen_jahr);
        const ausgabenJahr = num(payload.ausgaben_jahr);
        const jahresergebnis = num(payload.jahresergebnis);

        // Monatswerte aus dict {1..12} in Array transformieren
        const me = (payload.monatsergebnisse ?? {}) as Record<string, Record<string, unknown>>;
        const monatswerte: PlanSnapshot['monatswerte'] = [];
        let personalKosten = 0;
        let sachKosten = 0;
        let foerderung32 = 0;
        let elternBeitraege = 0;
        let zuschuesse = 0;
        for (let m = 1; m <= 12; m++) {
          const row = me[String(m)] ?? {};
          const einnahmen = num(row.einnahmen_summe);
          const ausgaben = num(row.ausgaben_summe);
          monatswerte.push({ monat: m, einnahmen, ausgaben, saldo: einnahmen - ausgaben });
          // ausgaben_personal_sonstiges gehoert zu den Personalkosten und
          // war bisher in KEINER der beiden Summen enthalten — nur in
          // ausgaben_summe. Solange dort nie Geld landete, fiel das nicht
          // auf; mit den Punkten "Sonstige Personalkosten" (0027) waere die
          // Aufteilung sonst kleiner als die Gesamtausgaben.
          personalKosten +=
            num(row.ausgaben_personal_gesamt) + num(row.ausgaben_personal_sonstiges);
          sachKosten +=
            num(row.ausgaben_material) +
            num(row.ausgaben_verpflegung) +
            num(row.ausgaben_geld) +
            num(row.ausgaben_raum) +
            num(row.ausgaben_verwaltung) +
            num(row.ausgaben_instandhaltung);
          foerderung32 += num(row.einnahmen_land_32);
          elternBeitraege += num(row.einnahmen_elternbeitraege);
          zuschuesse += num(row.einnahmen_kommune) + num(row.einnahmen_land_sonstige);
        }
        const kostenProPlatz = 0;
        const deckungsgrad = ausgabenJahr > 0 ? einnahmenJahr / ausgabenJahr : 0;

        return {
          plan_id: planId,
          calculated_at: res.data.created_at,
          calc_version: res.data.calc_engine_version,
          kpis: {
            einnahmen_gesamt: einnahmenJahr,
            ausgaben_gesamt: ausgabenJahr,
            saldo: jahresergebnis,
            personal_kosten: personalKosten,
            sach_kosten: sachKosten,
            foerderung_32: foerderung32,
            eltern_beitraege: elternBeitraege,
            zuschuesse,
            belegungsquote: 0,
            fachkraftquote: 0,
            vzaeq: 0,
            kosten_pro_platz: kostenProPlatz,
            deckungsgrad,
          } as PlanSnapshot['kpis'],
          monatswerte,
          warnings: [] as PlanSnapshot['warnings'],
        };
      } catch {
        return null;
      }
    },
    enabled: !!planId,
    staleTime: 5_000,
  });
}

/**
 * Backwards-compatible alias for the recalc mutation. Prefer importing
 * `useRecalcPlan` from `./usePlaene` in new code; this re-export exists so the
 * existing PlanEditorPage import continues to compile.
 */
export function useRecalcPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (planId: UUID): Promise<unknown> => {
      const res = await apiClient.post<unknown>(`/plan/${planId}/recalc`);
      return res.data;
    },
    onSuccess: (_data, planId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}
